// Fleet Reports — per-vehicle bar chart (2026-07-14).
//
// WHY A LOCAL WRAPPER instead of components/Charts/Bar:
//  1. Every existing amCharts wrapper builds with `[]` deps (Charts/Bar:98,
//     Vertical Bar:110, Clustered Column:118, TSChart:224) — the chart is built
//     ONCE on mount and NEVER updates when props.data changes. On this page
//     (pick a week -> Load -> new data) that is fatal: every chart would show
//     the first loaded week forever. The app's only workaround is to unmount and
//     remount the chart, which is not viable for the Fuel-Cost calculator in P3
//     (bound to live form state — it would rebuild on every keystroke).
//  2. Charts/Clustered Column keeps its am5.Root in a MODULE-SCOPE `var`, so two
//     mounted instances corrupt each other regardless of DOM id.
//  3. They key the root on a DOM id (`id={props.name}`), which forces globally
//     unique ids across a page that mounts many charts.
//
// This wrapper: useRef for the container (NO DOM id), root created inside
// useLayoutEffect with [data] in the deps, disposed in THAT effect's cleanup.
// Correct update semantics, N safe instances, no id coupling.
import React, { useLayoutEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

// data: [{ key, label, value, color }]
//
// `key` (the sensorId) identifies the bar; `label` is only what the axis shows.
// amCharts5 CategoryAxis resolves a datum to its slot BY CATEGORY VALUE, so
// keying on the display name would make two identically-named vehicles collapse
// into a single bar (nothing enforces name uniqueness). Same defect class as
// FleetLine, fixed the same way.
export default function FleetBar({ data, unit, height = "260px", valueFormat = "#.00" }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const root = am5.Root.new(ref.current);
    root.setThemes([am5themes_Animated.new(root)]);
    root._logo && root._logo.dispose();

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, { panX: false, panY: false, wheelX: "none", wheelY: "none", paddingLeft: 0 })
    );

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 20 });
    xRenderer.labels.template.setAll({ fontSize: 12 });
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, { categoryField: "key", renderer: xRenderer })
    );
    // Show the friendly name, but keep the identity on the sensorId.
    xRenderer.labels.template.adapters.add("text", (text, target) => {
      const d = target.dataItem && target.dataItem.dataContext;
      return d && d.label ? d.label : text;
    });
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, {}), min: 0 })
    );
    if (unit) {
      yAxis.children.unshift(
        am5.Label.new(root, { rotation: -90, text: unit, y: am5.p50, centerX: am5.p50, fontSize: 12, fill: am5.color("#666666") })
      );
    }

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        xAxis,
        yAxis,
        valueYField: "value",
        categoryXField: "key",
        tooltip: am5.Tooltip.new(root, { labelText: `{label}: {valueY.formatNumber('${valueFormat}')} ${unit || ""}` }),
      })
    );
    series.columns.template.setAll({ cornerRadiusTL: 4, cornerRadiusTR: 4, width: am5.percent(60) });
    // Per-datum RAG colour (set by the caller) — never tenant branding.
    series.columns.template.adapters.add("fill", (fill, target) =>
      target.dataItem && target.dataItem.dataContext.color
        ? am5.color(target.dataItem.dataContext.color)
        : fill
    );
    series.columns.template.adapters.add("stroke", (stroke, target) =>
      target.dataItem && target.dataItem.dataContext.color
        ? am5.color(target.dataItem.dataContext.color)
        : stroke
    );

    const clean = (data || []).filter((d) => d.value !== null && d.value !== undefined);
    xAxis.data.setAll(clean);
    series.data.setAll(clean);
    series.appear(600);

    return () => root.dispose();
  }, [data, unit, valueFormat]);

  return <div ref={ref} style={{ width: "100%", height }} />;
}
